<script setup lang="ts">
import { VTextField, VTextArea, VButton, VIcon } from '@/modules/shared/components'
import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import { computed, onMounted, ref, watch } from 'vue'
import { useExtendedI18n } from '@/i18n'
import { capitalize, cloneDeep, differenceWith, isEqual } from 'lodash'
import { format as formatDate, parseISO } from 'date-fns'
import { useTemplateStore } from '@/modules/signing/stores/template-store'
import VSelect from './v-select.vue'
import { createOptions, markDisabledOptions } from '@/modules/shared/utils/form'
import { useInvestingInvestorStore } from '../stores/investor-store'
import { useRoute, useRouter } from 'vue-router'
import { useEntityStore } from '../stores/entity-store'
import { useDocumentStore } from '../stores/document-store'
import IndexVSection from './index-v-section.vue'
import VDropdownMultiple from './v-dropdown-multiple.vue'

//////////////////////////
///// UTILS
//////////////////////////
const toSnakeCase = (str) => str.replace(' ', '_').toLowerCase()

const formatCommitmentDate = (dateValue: string | null | undefined): string => {
  if (!dateValue) return 'N/A'
  try {
    const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue)
    return formatDate(date, 'MMM d, yyyy')
  } catch {
    return dateValue
  }
}

const formatCommitmentAmount = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined || amount === '') return 'N/A'
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(numAmount)) return String(amount)
  return numAmount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}
const combine = (list: any[], n: number = 0, result: any[] = [], current: any[] = []) => {
  if (n === list.length) {
    result.push(current)
  } else {
    list[n].forEach((item: any) => combine(list, n + 1, result, [...current, item]))
  }

  return result
}
const getRoleWithMultipleSigner = (new_signers, old_signers) => {
  let hasAdded = false

  signer_roles.value.forEach((role) => {
    const newRoleSigners = new_signers[role] || []
    const oldRoleSigners = old_signers[role] || []

    // Detect added elements
    const addedSigners = differenceWith(newRoleSigners, oldRoleSigners, isEqual)
    if (addedSigners.length > 0) {
      hasAdded = true
    }
  })
  const role_w_multiple_signer = hasAdded
    ? signer_roles.value.find((role) => old_signers[role]?.length > 1)
    : signer_roles.value.find((role) => new_signers[role]?.length > 1)

  return role_w_multiple_signer
}
// Sort by investor name, then by sequence letter (A before B, etc.)
const sortByNameThenSequence = (a, b) => {
  const nameCompare = a.label.localeCompare(b.label)
  if (nameCompare !== 0) return nameCompare
  // Sort by sequence - use empty string as fallback so items without sequence come first
  return (a._sequence || '').localeCompare(b._sequence || '')
}

const generateOption = (investor, admin = null, type = null, showInvestorName = true) => {
  if (admin) {
    return {
      label: showInvestorName ? `${investor.name} - ${admin.name}` : `${admin.name}`,
      value: `${investor._cid}_${admin._cid}`,
      type: type || 'Admin',
    }
  } else {
    if (Array.isArray(investor.commitments) && investor.commitments.length > 0) {
      const processedInvestors = investor.commitments.map((commitment) => {
        const sequenceLabel = commitment.investor_sequence ? `Seq ${commitment.investor_sequence}` : null
        const dateLabel = formatCommitmentDate(commitment.date)
        const amountLabel = formatCommitmentAmount(commitment.capital_amount)
        const sublabelParts = [sequenceLabel, dateLabel, amountLabel].filter(Boolean)

        return {
          label: investor.name || 'N/A',
          sublabel: sublabelParts.join(' • '),
          value: `${commitment._cid}_${investor._cid}`,
          type: 'Investor',
          _sequence: commitment.investor_sequence || '',
        }
      })
      return processedInvestors
    } else {
      return [
        {
          label: investor.name,
          value: investor._cid,
          type: 'Investor',
        },
      ]
    }
  }
}
const generateSectionLabel = (label) => {
  return {
    label: label,
    value: '',
    section_label: true,
  }
}

//////////////////////////
///// MAIN
//////////////////////////
const { t } = useExtendedI18n()
const route = useRoute()
const router = useRouter()

const props = defineProps<{
  investor_type: 'spv' | 'fund' | 'gp' | 'group'
  investor_id: string | number
}>()

const documentStore = useDocumentStore()
const investorStore = useInvestingInvestorStore()
const templateStore = useTemplateStore()
const entityStore = useEntityStore()

const selected_template = ref(null)
const currentSignerTabIndex = ref(0)
const signers_errors = ref(null)

onMounted(async () => {
  await Promise.all([
    templateStore.fetchTemplates(props.investor_type, props.investor_id),
    investorStore.fetchInvestors(),
  ])
  if (['spv', 'fund', 'gp'].includes(props.investor_type)) {
    await entityStore.fetchEntity({
      id: props.investor_id,
      type: props.investor_type,
    })
  }
  if (route.query.provider_template_id) {
    selected_template.value = templateStore.templates.find(
      (template) => template.provider_template_id === route.query.provider_template_id,
    )
    if (selected_template.value) document_form.value.template_id = selected_template.value.id
  }
  signer_roles.value.forEach((role) => {
    document_form.value.signers[role] = []
  })
})

//////////////////////////
///// COMPUTED
//////////////////////////
const cid = computed(() => `${props.investor_type}:${props.investor_id}`)
const currentEntity = computed(() => entityStore.items.get(cid.value))
const templateOptions = computed(() => {
  const disabledTemplateIds = Array.from(templateStore.template_items.values())
    .filter((template) => !template.usable_in_document_form)
    .map((template) => template.id)
  return markDisabledOptions(createOptions(templateStore.template_items, { label: 'name' }), disabledTemplateIds)
})
const titlePlaceholder = computed(() => {
  if (selected_template.value?.name === 'Investor Subscription Document Packet') {
    return `Investor Subscription Agreement ${currentEntity.value?.name || ''}`
  }
  return selected_template.value?.name || ''
})
const entity_admin_cids = computed(
  () => currentEntity.value?.managers?.map((manager) => `individual:${manager.id}`) || [],
)
const investorOptions = computed(() => {
  const investors = investorStore.investors.filter((investor) => !['spv', 'fund', 'gp'].includes(investor._custom_type))
  const investor_options = []

  if (props.investor_type === 'group') {
    investor_options.push(generateSectionLabel('Individuals'))
    investor_options.push(
      investors
        .filter((investor) => investor._custom_type === 'individual')
        .map((investor) => generateOption(investor))
        .flat()
        .sort(sortByNameThenSequence),
    )
    investor_options.push(generateSectionLabel('Other Entities'))
    investor_options.push(
      investors
        .filter((investor) => investor._custom_type !== 'individual')
        .map((investor) => investor.admins.map((admin) => generateOption(investor, admin)))
        .flat()
        .sort((a, b) => a.label.localeCompare(b.label)),
    )
  } else {
    // Admins section: Group admins + SPV/Entity admins
    investor_options.push(generateSectionLabel('Admins'))
    const adminOptions = investors
      .filter((investor) => investor._custom_type === 'individual')
      .reduce((acc, investor) => {
        // Include group admins
        if (investor.is_group_admin) {
          acc.push(generateOption(currentEntity.value, investor, 'Group Admin', false))
        }
        // Include SPV/Entity admins (managers)
        else if (entity_admin_cids.value.includes(investor._cid)) {
          acc.push(generateOption(currentEntity.value, investor, 'Admin', false))
        }
        return acc
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label))
    investor_options.push(adminOptions)

    // Investors section: Only shareholders with commitments to this entity
    // Use entity's investors data which has commitments already scoped to this entity
    investor_options.push(generateSectionLabel('Investors'))
    const entityInvestors = currentEntity.value?.investors || []
    const investorOptionsForEntity = entityInvestors
      .reduce((acc, entityInvestor) => {
        const investor = investorStore.investorItems.get(entityInvestor._cid)
        if (!investor) return acc

        const commitment = entityInvestor.commitment

        if (investor._custom_type === 'individual') {
          // Show commitment details for individual investors with formatted sublabel
          const sequenceLabel = commitment.investor_sequence ? `Seq ${commitment.investor_sequence}` : null
          const dateLabel = formatCommitmentDate(commitment.date)
          const amountLabel = formatCommitmentAmount(commitment.capital_amount)
          const sublabelParts = [sequenceLabel, dateLabel, amountLabel].filter(Boolean)

          acc.push({
            label: entityInvestor.name || 'N/A',
            sublabel: sublabelParts.join(' • '),
            value: `${commitment._cid}_${entityInvestor._cid}`,
            type: 'Investor',
            _sequence: commitment.investor_sequence || '',
          })
        } else {
          // For entity investors, show each admin with commitment info
          const sequenceLabel = commitment.investor_sequence ? `Seq ${commitment.investor_sequence}` : null
          const dateLabel = formatCommitmentDate(commitment.date)
          const amountLabel = formatCommitmentAmount(commitment.capital_amount)
          const sublabelParts = [sequenceLabel, dateLabel, amountLabel].filter(Boolean)

          investor.admins.forEach((admin) => {
            acc.push({
              label: `${entityInvestor.name} - ${admin.name}`,
              sublabel: sublabelParts.join(' • '),
              value: `${commitment._cid}_${entityInvestor._cid}_${admin._cid}`,
              type: 'Admin',
              _sequence: commitment.investor_sequence || '',
            })
          })
        }
        return acc
      }, [])
      .sort(sortByNameThenSequence)
    investor_options.push(investorOptionsForEntity)

    // Other Group Investors section: Investors in the group not already shown in Admin or Investors sections
    const entityInvestorCids = new Set(entityInvestors.map((inv) => inv._cid))
    const otherGroupInvestors = investors.filter(
      (investor) =>
        !entityInvestorCids.has(investor._cid) && // Not in Investors section
        !investor.is_group_admin && // Not a group admin
        !entity_admin_cids.value.includes(investor._cid), // Not an entity admin
    )

    if (otherGroupInvestors.length > 0) {
      investor_options.push(generateSectionLabel('Other Group Investors'))
      // Individual investors - no commitment info shown
      investor_options.push(
        otherGroupInvestors
          .filter((investor) => investor._custom_type === 'individual')
          .map((investor) => ({
            label: investor.name || 'N/A',
            value: `${investor._cid}`,
            type: 'Investor',
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      )
      // Entity investors (funding-entity, etc.) - show each admin, no commitment info
      investor_options.push(
        otherGroupInvestors
          .filter((investor) => investor._custom_type !== 'individual')
          .map(
            (investor) =>
              investor.admins?.map((admin) => ({
                label: `${investor.name} - ${admin.name}`,
                value: `${investor._cid}_${admin._cid}`,
                type: 'Admin',
              })) || [],
          )
          .flat()
          .sort((a, b) => a.label.localeCompare(b.label)),
      )
    }
  }

  return investor_options.flat()
})
const signer_roles = computed(() => selected_template.value?.signing_roles?.map((role) => toSnakeCase(role)) || [])
const signer_tabs = computed(() =>
  signer_roles.value.filter((role) => role.length !== 0).map((role) => capitalize(`${role} role`)),
)

//////////////////////////
///// FORM
//////////////////////////
const initalState = {
  template_id: null,
  signers: {},
  title: null,
  message: null,
}
const rules = {
  template_id: { required },
}
const document_form = ref({ ...initalState })
const v$ = useVuelidate(rules, document_form, { $lazy: true })
const generateSigner = (investor_cid, investor_admin_cid, signer_role, commitment_cid) => {
  let investor = investorStore.investorItems.get(investor_cid)
  if (!investor) {
    throw new Error(`Signer not found: ${investor_cid}`)
  }

  let investor_admin = null
  let commitment = null

  // Find commitment if commitment_cid is provided
  if (commitment_cid) {
    // First try to find commitment from the entity store's investors list (for SPV/Fund investors)
    const entityInvestor = currentEntity.value?.investors?.find((inv) => inv.commitment?._cid === commitment_cid)
    if (entityInvestor?.commitment) {
      commitment = entityInvestor.commitment
    } else {
      // Fall back to investor store's commitments (for group-level investors)
      commitment = investor.commitments?.find((c) => c._cid === commitment_cid)
    }
  }

  // Find admin if investor_admin_cid is provided
  if (investor_admin_cid) {
    // Check if investor_cid refers to the current entity (SPV/Fund) - if so, the admin IS the signer
    // Use String() coercion to handle string/number type mismatch between route params and API data
    const isCurrentEntity =
      props.investor_type === investor._custom_type && String(props.investor_id) === String(investor.id)

    if (isCurrentEntity) {
      // Admin is signing as themselves (e.g., Group Admin or Entity Admin)
      investor = investorStore.investorItems.get(investor_admin_cid)
      if (!investor) {
        throw new Error(`Signer not found: ${investor_admin_cid}`)
      }
    } else {
      // Admin is signing on behalf of an entity investor
      investor_admin = investor.admins?.find((admin) => admin._cid === investor_admin_cid)
      if (!investor_admin) {
        throw new Error(`Admin signer not found: ${investor_admin_cid} for investor ${investor_cid}`)
      }
    }
  }

  const role = selected_template.value?.signing_roles.find((role) => toSnakeCase(role) === signer_role)

  if (investor_admin) {
    return {
      investor_id: investor.id,
      commitment_id: commitment ? commitment.id : null,
      investor_type: 'InvestorSet',
      signer_id: investor_admin.id,
      name: `${investor.name.trim()} - ${investor_admin.name.trim()}`,
      email_address: investor_admin.email,
      type: 'admin',
      cc: [],
      role: role,
    }
  } else {
    return {
      investor_id: investor.id,
      commitment_id: commitment ? commitment.id : null,
      investor_type: 'Investor',
      signer_id: investor.id,
      name: investor.name.trim(),
      email_address: investor.email,
      type: 'investor',
      cc: [],
      role: role,
    }
  }
}
const saveDocument = async () => {
  // validate basic info of form
  let is_valid = await v$.value.$validate()
  if (!is_valid) return
  // validate signers
  for (const role of signer_roles.value) {
    if (document_form.value.signers[role].length < 1) {
      signers_errors.value = `Please select at least one investor for each role to continue.`
      return
    }
  }

  // prettier-ignore
  if (!window.confirm(
      'Are you sure? This will send legally binding signature requests to every signer selected. Reminder emails will be sent every 3 days for the next 120 days or until signed.',
  ))
    return

  // transform data
  const signers = signer_roles.value.map((role) => {
    return document_form.value.signers[role].map((signer) => {
      const has_commitment = signer.includes('commitment:')
      const parts = signer.split('_')

      let commitment_cid = null
      let investor_cid = null
      let investor_admin_cid = null

      if (has_commitment && parts.length === 3) {
        // Entity investor with commitment: commitment_cid_investor_cid_admin_cid
        commitment_cid = parts[0]
        investor_cid = parts[1]
        investor_admin_cid = parts[2]
      } else if (has_commitment && parts.length === 2) {
        // Individual investor with commitment: commitment_cid_investor_cid
        commitment_cid = parts[0]
        investor_cid = parts[1]
      } else {
        // Legacy format without commitment: investor_cid_admin_cid
        investor_cid = parts[0]
        investor_admin_cid = parts[1]
      }

      return generateSigner(investor_cid, investor_admin_cid, role, commitment_cid)
    })
  })
  const signer_list = combine(signers)
  const payload = {
    batchable_id: props.investor_id,
    batchable_type: props.investor_type === 'group' ? 'Group' : 'InvestorSet',
    email_message: document_form.value.message,
    title: document_form.value.title,
    template_id: document_form.value.template_id,
    signer_list: signer_list,
    signers: signers.flat(),
  }

  await documentStore.addDocument(payload)

  if (props.investor_type === 'group') {
    router.push({ name: 'investing.documents' })
  } else {
    router.push({ name: 'investing.entity.documents' })
  }
}

//////////////////////////
///// WATCH
//////////////////////////
watch(
  () => cloneDeep(document_form.value.signers), // Create a deep copy to avoid reference issues
  (new_signers, old_signers) => {
    if (isEqual(new_signers, old_signers)) return
    if (Object.keys(old_signers).length === 0) return

    const role_w_multiple_signer = getRoleWithMultipleSigner(new_signers, old_signers)

    if (role_w_multiple_signer) {
      signer_roles.value.forEach((role) => {
        if (document_form.value.signers[role].length > 1 && role !== role_w_multiple_signer) {
          document_form.value.signers[role] = new_signers[role].slice(0, -1)
          signers_errors.value = `${capitalize(
            role_w_multiple_signer,
          )} role already has multiple signers. You can't add more for ${capitalize(role)} role.`
        }
      })
    } else {
      signers_errors.value = null
    }
  },
  { deep: true },
)
watch(
  () => document_form.value.template_id,
  (new_template_id) => {
    selected_template.value = templateStore.templates.find((template) => template.id === new_template_id)
    signer_roles.value.forEach((role) => {
      document_form.value.signers[role] = []
    })
    // Auto-fill message from template
    document_form.value.message = selected_template.value?.message || null
  },
)
</script>

<template>
  <IndexVSection class="flex gap-14 pb-10" index="1">
    <div class="w-1/2 pr-5">
      <h3 class="text-lg font-[500] text-gray-900 lg:text-lg">Select template</h3>
      <p class="mt-3 text-sm">Choose a template you've already built</p>
    </div>
    <div class="w-1/2">
      <div class="flex flex-col gap-3">
        <VSelect
          v-model="document_form.template_id"
          :disabled="false"
          :options="templateOptions"
          property="template_id"
          :v$="v$"
          size="sm"
          class="flex-grow"
        />
      </div>
    </div>
  </IndexVSection>
  <IndexVSection class="flex gap-14 pb-10" index="2">
    <div class="w-1/2 pr-5">
      <h3 class="text-lg font-[500] text-gray-900 lg:text-lg">Add signers</h3>
      <p class="mt-3 text-sm">
        Each role requires at least one signer to continue. Add multiple signers to a role to batch initiate multiple
        signature requests.
      </p>
    </div>
    <div class="w-1/2 space-y-5" v-if="selected_template">
      <div class="mb-3 border-[1px] bg-red-100 p-2 text-sm text-red-700 ring-red-200/75" v-if="signers_errors">
        {{ signers_errors }}
      </div>
      <div class="border-b border-gray-200 dark:border-zinc-800">
        <nav class="relative -mb-px flex space-x-3 sm:space-x-6" aria-label="Tabs">
          <div
            v-for="(tab, index) in signer_tabs"
            :key="tab"
            @click.prevent="currentSignerTabIndex = index"
            class="sm:text-md relative flex cursor-pointer overflow-x-clip whitespace-nowrap border-b py-1.5 font-medium leading-7"
            :class="[
              index === currentSignerTabIndex
                ? 'border-current font-semibold text-[#3b88af]'
                : 'border-transparent font-medium text-gray-700 hover:border-gray-300 dark:text-zinc-300',
              'text-sm',
            ]"
          >
            <div class="flex items-center gap-3">
              <div>{{ tab }}</div>
              <div
                class="h-5 w-5 rounded pt-[3px] text-center text-xs font-normal text-black"
                :class="[index === currentSignerTabIndex ? 'bg-sky-100' : 'bg-gray-100']"
              >
                {{ document_form.signers[signer_roles[index]].length }}
              </div>
            </div>
            <div
              v-if="index === currentSignerTabIndex"
              class="pointer-events-none absolute -left-4 top-[41px] flex h-8 rotate-180 items-end overflow-hidden"
            >
              <div class="-mb-px flex h-[2px] w-24 rotate-180">
                <div
                  class="w-full flex-none blur-sm [background-image:linear-gradient(-90deg,rgba(56,189,248,0)_0%,#0EA5E9_32.29%,rgba(236,72,153,0.3)_67.19%,rgba(236,72,153,0)_100%)]"
                ></div>
                <div
                  class="-ml-[100%] w-full flex-none blur-[1px] [background-image:linear-gradient(90deg,rgba(56,189,248,0)_0%,#0EA5E9_32.29%,rgba(236,72,153,0.3)_67.19%,rgba(236,72,153,0)_100%)]"
                ></div>
              </div>
            </div>
          </div>
        </nav>
      </div>
      <VDropdownMultiple
        aligned="left"
        class="z-auto"
        :options="investorOptions"
        v-model="document_form.signers[signer_roles[currentSignerTabIndex]]"
        :show-selected-list="true"
        v-for="role in signer_roles"
        v-show="signer_roles[currentSignerTabIndex] === role"
        :key="role"
      >
        <VButton class="w-fit">
          <div class="flex items-center justify-between gap-3">
            <div>Add signer</div>
            <VIcon name="ChevronSelectorVertical" />
          </div>
        </VButton>
      </VDropdownMultiple>
    </div>
  </IndexVSection>
  <IndexVSection class="flex gap-14 pb-10" index="3">
    <div class="w-1/2 pr-5">
      <h3 class="text-lg font-[500] text-gray-900 lg:text-lg">Document Details</h3>
      <p class="mt-3 text-sm">
        Rename the document, Note this is optional and if left blank, the document name will match the template.
      </p>
    </div>
    <div class="w-1/2 space-y-5">
      <VTextField
        v-model="document_form.title"
        :label="capitalize(t('shared.document title'))"
        property="title"
        :placeholder="titlePlaceholder"
        :v$="v$"
      />
      <VTextArea
        :label="capitalize(t('shared.note'))"
        v-model="document_form.message"
        :v$="v$"
        property="message"
        :placeholder="selected_template?.message"
      />
    </div>
  </IndexVSection>
  <IndexVSection class="flex items-center gap-10 pb-10" index="4">
    <div class="w-1/2 pr-5">
      <h3 class="text-lg font-[500] text-gray-900 lg:text-lg">Finalize</h3>
      <p class="mt-3 text-sm">
        Each signer will recieve an email requesting their signature.
        <span class="underline">Reminder emails will be sent every 3 days for the next 120 days or until signed.</span>
      </p>
    </div>
    <div class="w-1/2">
      <VButton variant="v-blue" size="lg" :click="saveDocument"> Confirm and request signatures </VButton>
    </div>
  </IndexVSection>
</template>
